import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { House, PencilSimple, GameController } from '@phosphor-icons/react';
import { GiSpartanHelmet, GiCrossedSwords } from 'react-icons/gi';
import { VortexIcon } from '@/pages/vortex/components/VortexIcon';
import { useAuthStore } from '@/stores/authStore';
import { PerfilMenu } from './PerfilMenu';
import './MadBoatNav.css';

type NavTabId = 'home' | 'odisseia' | 'diario' | 'deck' | 'vortex' | 'fliperama' | 'perfil';

interface NavTab {
  id: NavTabId;
  labelKey: string;
  route: string | null;
}

const tabs: NavTab[] = [
  { id: 'home', labelKey: 'nav.home', route: '/dashboard' },
  { id: 'odisseia', labelKey: 'nav.odisseia', route: '/odisseia' },
  { id: 'diario', labelKey: 'nav.diario', route: '/diario' },
  { id: 'deck', labelKey: 'nav.deck', route: '/deck' },
  { id: 'vortex', labelKey: 'nav.vortex', route: '/vortex' },
  { id: 'fliperama', labelKey: 'nav.fliperama', route: null },
  { id: 'perfil', labelKey: 'nav.perfil', route: null },
];

const tabIcons: Record<NavTabId, { icon: React.ElementType; size: number }> = {
  home: { icon: House, size: 18 },
  odisseia: { icon: GiSpartanHelmet, size: 18 },
  diario: { icon: PencilSimple, size: 18 },
  deck: { icon: GiCrossedSwords, size: 18 },
  vortex: { icon: VortexIcon, size: 18 },
  fliperama: { icon: GameController, size: 18 },
  perfil: { icon: House, size: 0 }, // Avatar used instead
};

interface MadBoatNavProps {
  hidden?: boolean;
}

export function MadBoatNav({ hidden }: MadBoatNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showPerfilMenu, setShowPerfilMenu] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const perfilRef = useRef<HTMLDivElement>(null);

  // Close perfil menu when clicking outside
  useEffect(() => {
    if (!showPerfilMenu) return;
    function handleClickOutside(event: MouseEvent) {
      if (perfilRef.current && !perfilRef.current.contains(event.target as Node)) {
        setShowPerfilMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPerfilMenu]);

  // Listen for carousel index changes from dashboard
  useEffect(() => {
    const handleCarouselIndex = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.index === 'number') {
        setCarouselIndex(detail.index);
      }
    };
    window.addEventListener('madboat:carousel-index', handleCarouselIndex);
    return () => window.removeEventListener('madboat:carousel-index', handleCarouselIndex);
  }, []);

  // Reset carousel index when leaving dashboard
  useEffect(() => {
    if (location.pathname !== '/dashboard') {
      setCarouselIndex(0);
    }
  }, [location.pathname]);

  // Determine active tab from current path + carousel index
  const getActiveTab = (): NavTabId | null => {
    const path = location.pathname;
    if (path === '/dashboard') {
      // On dashboard, check carousel index for diario/fliperama
      if (carouselIndex === 2) return 'diario';
      if (carouselIndex === 4) return 'fliperama';
      return 'home';
    }
    if (path === '/odisseia' || path.startsWith('/odisseia/')) return 'odisseia';
    if (path === '/diario' || path.startsWith('/diario/')) return 'diario';
    if (path === '/deck' || path.startsWith('/deck/') || path.startsWith('/u/')) return 'deck';
    if (path === '/vortex') return 'vortex';
    if (path === '/jornadas' || path.startsWith('/jornada/')) return 'odisseia';
    if (path === '/conquistas') return 'odisseia';
    return null;
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab: NavTab) => {
    if (tab.id === 'perfil') {
      setShowPerfilMenu(!showPerfilMenu);
      return;
    }

    // Close perfil menu if open
    setShowPerfilMenu(false);

    // Fliperama: navigate to dashboard + scroll carousel to index 4
    if (tab.id === 'fliperama') {
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('madboat:carousel-goto', { detail: { index: 4 } }));
      }, 100);
      return;
    }

    // Diario: navigate to dashboard + scroll carousel to diario-bordo
    if (tab.id === 'diario') {
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('madboat:carousel-goto', { detail: { index: 2 } }));
      }, 100);
      return;
    }

    // Home: always go to dashboard card 0 (Canal do Tripulante)
    if (tab.id === 'home') {
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('madboat:carousel-goto', { detail: { index: 0 } }));
      }, 100);
      return;
    }

    // Navigate directly
    if (tab.route) {
      navigate(tab.route);
    }
  };

  if (hidden) return null;

  return (
    <nav className="madboat-nav" role="navigation" aria-label={t('nav.home')}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const iconInfo = tabIcons[tab.id];
        const label = t(tab.labelKey);

        if (tab.id === 'perfil') {
          return (
            <div key={tab.id} className="madboat-nav-perfil-wrap" ref={perfilRef}>
              <button
                className={`madboat-nav-tab ${isActive ? 'active' : ''} perfil-tab`}
                onClick={() => handleTabClick(tab)}
                aria-label={label}
                aria-expanded={showPerfilMenu}
              >
                <PerfilAvatar />
                <span className="madboat-nav-label">{label}</span>
              </button>
              {showPerfilMenu && (
                <PerfilMenu onClose={() => setShowPerfilMenu(false)} />
              )}
            </div>
          );
        }

        const Icon = iconInfo.icon;
        return (
          <button
            key={tab.id}
            className={`madboat-nav-tab ${isActive ? 'active' : ''} ${tab.id}-tab`}
            onClick={() => handleTabClick(tab)}
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon className="madboat-nav-icon" size={iconInfo.size} />
            <span className="madboat-nav-label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Perfil Avatar — shows user initials
 */
function PerfilAvatar() {
  const user = useAuthStore((s) => s.user);

  const initials = (() => {
    if (!user?.name) return '?';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return user.name[0].toUpperCase();
  })();

  return (
    <div className="madboat-nav-avatar">
      <span>{initials}</span>
    </div>
  );
}
